import { CloseOutlined, InboxOutlined } from '@ant-design/icons';
import { Form, Modal } from 'antd';
import Title from 'antd/es/typography/Title';
import Upload, { UploadChangeParam, UploadFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { holidayApi } from 'Api/masters/holiday';
import Buttons from 'components/Global/Buttons';
import csvToJson from 'csvtojson';
import React, { useState } from 'react';
import { DownloadSvg } from 'utils/svgs';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';
import { AddHolidaysProps } from './types';

const AddHolidayModal: React.FC<AddHolidaysProps> = (props) => {
	const {
		handleCancel,
		isAddHolidayModalOpen,
		fetchHolidays,
		showAddHolidayManuallyModal,
	} = props;

	const [file, setFile] = useState<any>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isUploadLoading, setIsUploadLoading] = useState(false);

	const uploadCsv = async (data: any) => {
		try {
			setIsUploadLoading(true);

			// Detect file type
			const fileExtension = data.Attachment.name
				.substring(data.Attachment.name.lastIndexOf('.'))
				.toLowerCase();
			const mimeType = data.Attachment.type;

			const isExcel =
				fileExtension === '.xlsx' ||
				fileExtension === '.xls' ||
				mimeType ===
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
				mimeType === 'application/vnd.ms-excel';

			if (isExcel) {
				// For Excel: Send file to backend for parsing
				const formData = new FormData();
				formData.append('file', data.Attachment);

				try {
					await holidayApi.uploadFile(formData);
					fetchHolidays();
					toastText('File uploaded successfully', 'success');
					closeModal();
				} catch (err: any) {
					const errorMessage =
						err?.response?.data?.message ||
						'Something went wrong in uploading file';
					toastText(errorMessage, 'error');
				} finally {
					setIsUploadLoading(false);
				}
			} else {
				// For CSV: Keep existing frontend parsing for backward compatibility
				const reader = new FileReader();
				reader.onload = async (e: any) => {
					try {
						const csvData = e.target.result;

						const lines = csvData
							.split('\n')
							.filter((line: string) => line.trim() !== '');

						if (lines.length === 0) {
							toastText('The file is empty', 'error');
							setIsUploadLoading(false);
							return;
						}

						const headers = lines[0]
							.split(',')
							.map((header: string) => header.trim());
						const expectedHeaders = ['Date', 'Holiday Name'];

						if (
							headers.length !== expectedHeaders.length ||
							!headers.every(
								(header: any, index: any) =>
									header === expectedHeaders[index]
							)
						) {
							toastText(
								'Invalid headers. Expected headers: Date, Holiday Name',
								'error'
							);
							setIsUploadLoading(false);
							return;
						}

						if (lines.length <= 1) {
							toastText(
								'The file is empty - no data rows found',
								'error'
							);
							setIsUploadLoading(false);
							return;
						}

						// Parse CSV data
						const jsonData = await csvToJson({
							headers, // Use the validated headers
							includeColumns: /./, // Include all columns
						}).fromString(csvData);

						// Filter out empty rows and validate data
						const validData: any[] = [];
						const errors: string[] = [];

						jsonData.forEach((row, index) => {
							const hasAnyData = Object.values(row).some(
								(value) =>
									value !== null &&
									value !== undefined &&
									String(value).trim() !== ''
							);

							if (!hasAnyData) {
								return; // Skip empty rows
							}

							const obj: any = {};
							let hasError = false;

							Object.keys(row).forEach((key) => {
								const trimmedKey = key.trim();
								const value = row[key];

								if (
									value === null ||
									value === undefined ||
									String(value).trim() === ''
								) {
									errors.push(
										`Missing '${trimmedKey}' in row ${
											index + 1
										}`
									);
									hasError = true;
								}

								obj[trimmedKey] = value;
							});

							if (!hasError) {
								validData.push(obj);
							}
						});
						if (errors.length > 0) {
							const errorMessage =
								errors.length === 1
									? errors[0]
									: (() => {
											const moreErrorsText =
												errors.length > 5
													? `\n...and ${
															errors.length - 5
													  } more`
													: '';
											return `Found ${
												errors.length
											} issues:\n- ${errors
												.slice(0, 5)
												.join('\n- ')}${moreErrorsText}`;
									  })();

							toastText(errorMessage, 'error');
							setIsUploadLoading(false);
							return;
						}

						if (validData.length === 0) {
							toastText(
								'Empty file uploaded. Please try again',
								'error'
							);
							setIsUploadLoading(false);
							return;
						}

						try {
							await holidayApi.uploadCsv({
								holidays: validData,
							});

							fetchHolidays();
							toastText('CSV uploaded successfully', 'success');
							closeModal();
						} catch (err: any) {
							const errorMessage =
								err?.response?.data?.message ||
								'Something went wrong in uploading CSV';

							toastText(errorMessage, 'error');
						} finally {
							setIsUploadLoading(false);
						}
					} catch (parseError) {
						toastText(
							'Error parsing CSV file. Please check the file format.',
							'error'
						);
						setIsUploadLoading(false);
					}
				};

				reader.onerror = () => {
					toastText('Error reading the file', 'error');
					setIsUploadLoading(false);
				};

				reader.readAsText(data.Attachment);
			}
		} catch (err) {
			toastText('Something went wrong in uploading file', 'error');
			setIsUploadLoading(false);
		}
	};

	const closeModal = () => {
		setFile(null);
		setFileList([]);
		isRemoving = false;
		handleCancel();
	};

	const handleSubmit = async () => {
		try {
			if (!file) {
				toastText('Please select CSV or Excel file', 'error');
				return;
			}
			const data = {
				Attachment: file,
			};
			setIsLoading(true);
			uploadCsv(data);
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				'Something went wrong in uploading csv';
			toastText(message, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	let isRemoving = false;

	const propsUpload = {
		name: 'file',
		accept: '.csv,.xlsx,.xls',
		maxCount: 1,
		fileList: fileList,
		beforeUpload: (file: UploadFile) => {
			if (!file) {
				return;
			}
			// Validate file type
			const fileExtension = file.name
				.substring(file.name.lastIndexOf('.'))
				.toLowerCase();
			const mimeType = file.type;

			const isCSVOrExcel =
				fileExtension === '.csv' ||
				fileExtension === '.xlsx' ||
				fileExtension === '.xls' ||
				mimeType === 'text/csv' ||
				mimeType ===
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
				mimeType === 'application/vnd.ms-excel';

			if (!isCSVOrExcel) {
				toastText(
					'Invalid file format. Please upload CSV or Excel file',
					'error'
				);
				return Upload.LIST_IGNORE;
			}

			const isLt1M = file.size! / 1024 / 1024 < 5;
			if (!isLt1M) {
				toastText('File must be smaller than 5MB!', 'error');
				return Upload.LIST_IGNORE;
			}

			setFile(file);
			setFileList([file]);

			return false; // Prevents automatic upload
		},
		onChange(info: UploadChangeParam<UploadFile>) {
			const { file } = info;
			if (!isRemoving) {
				setFile(file);
				setFileList([file]);
			} else {
				isRemoving = false;
				setFile(null);
				setFileList([]);
			}
		},
		onDrop: () => {
			setFile(null);
			setFileList([]);
		},
		onRemove: () => {
			isRemoving = true;
		},
	};

	const handleManualHoliday = () => {
		showAddHolidayManuallyModal();
		closeModal();
	};

	const myButtons = [
		{
			text: 'Upload',
			isLoading: isUploadLoading,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: false,
			disabled: isLoading || isUploadLoading,
			onclick: () => {
				handleSubmit();
			},
		},
	];

	const addManualButton = [
		{
			text: 'Add Holiday manually',
			isLoading: false,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			disabled: isLoading || isUploadLoading,
			minHeight: '4rem',
			isSubmit: false,
			onclick: () => {
				handleManualHoliday();
			},
		},
	];

	const downloadCsv = async () => {
		try {
			const response = await holidayApi.getCsvTemplate();
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
			);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `holidays.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toastText(
				'Something went wrong in downloading csv template',
				'error'
			);
		}
	};

	return (
		<Modal
			open={isAddHolidayModalOpen}
			onOk={handleSubmit}
			onCancel={closeModal}
			maskClosable={true}
			okText={'Save'}
			closable={false}
			width={600}
			footer={null}
			className="addHolidayModal"
		>
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<Title level={4}>Add Holiday</Title>
					<div className={styles['right-align-items']}>
						<div>
							<p
								className={styles['template-link']}
								onClick={downloadCsv}
							>
								<DownloadSvg />
								Download Template
							</p>
						</div>
						<div
							className={styles['close-icon']}
							onClick={closeModal}
						>
							<CloseOutlined />
						</div>
					</div>
				</div>
				<div>
					<div className={styles['modalUploadContainer']}>
						<Form.Item name="file">
							<Dragger {...propsUpload} className="test">
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-hint">
									Drag and drop a file or choose file from
									Device Supported file formats.
									<br />
									CSV/Excel |{' '}
									<span className="color-purple">
										Maximum file size: 5MB
									</span>
								</p>
							</Dragger>
						</Form.Item>
					</div>

					<div className={styles['buttons']}>
						<Buttons buttons={myButtons} />
					</div>

					<div className={styles['centerLabel']}>
						<h3>OR</h3>
					</div>
					<div className={styles['centerButtons']}>
						<Buttons buttons={addManualButton} />
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default AddHolidayModal;
